import React, { useState, useContext, useMemo } from "react";
import authFetch from "../../authFetch";
import SearchableSelect from "../../shared/SearchableSelect";
import Modal from "../../shared/Modal";
import PlatformCtx from "../../state/PlatformCtx";
import { getEffectiveTaxonomy, getChildTaxonomy } from "../../permissions/checks";

function TaskCreateModal({state,dispatch,t,onClose,moduleFilter}){
  const {taskConfigs=[],users=[],locations=[],locFilter,lang}=state;
  const isRTL=lang==="ar";
  const API=typeof window!=="undefined"?window.location.origin:"";
  const platformCtx=useContext(PlatformCtx);
  const [form,setForm]=useState({title:"",titleAr:"",description:"",descriptionAr:"",sourceModule:moduleFilter||"",categoryId:"",typeId:"",taskType:"",assignedTo:"",locId:locFilter||locations[0]?.id||"",priority:"medium",dueDate:"",scheduledTime:""});
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState("");

  /* Module options: five source modules + Menu (wired in Phase 6).
     Suppliers / Staff are deliberately omitted per Q4 (reserve room but
     don't wire yet). Projects is included since it's a first-class source. */
  const TASK_MODULES=["Projects","Household","Facility","Fleet","Transport","Menu"];
  const moduleOptions=TASK_MODULES.map(m=>({value:m,label:(state.platformModuleNames||{})[m]||m}));

  /* Phase 3.1 — taxonomy-driven type dropdowns. Facility gets a
     two-level cascade (Category → Type); other modules get a single
     flat dropdown of root entries. */
  const taxonomy=useMemo(()=>getEffectiveTaxonomy(state,platformCtx,form.sourceModule),[state,platformCtx,form.sourceModule]);
  const isTwoLevel=form.sourceModule==="Facility";
  const categories=taxonomy.filter(e=>!e.parent);
  const children=isTwoLevel?getChildTaxonomy(state,platformCtx,form.categoryId):[];
  const typeEntries=isTwoLevel?children:categories;
  const lbl=(e)=>!e?"":(isRTL&&e.labelAr?e.labelAr:e.label);
  const selectedEntry=taxonomy.find(e=>e.id===form.typeId)||null;
  const categoryOptions=categories.map(c=>({value:c.id,label:lbl(c)}));
  const typeOptions=typeEntries.map(e=>({value:e.id,label:lbl(e)}));

  /* Legacy taskConfigs match — still works via the value string. */
  const matchedConfig=taskConfigs.find(c=>c.sourceModule===form.sourceModule&&c.taskType===form.taskType&&c.enabled);

  const priorityOptions=[
    {value:"low",label:isRTL?"منخفض":"Low"},
    {value:"medium",label:isRTL?"متوسط":"Medium"},
    {value:"high",label:isRTL?"عالي":"High"},
    {value:"urgent",label:isRTL?"عاجل":"Urgent"},
  ];
  const assigneeOptions=users.filter(u=>u.active).map(u=>({value:u.id,label:u.name}));
  const locationOptions=locations.map(l=>({value:l.id,label:isRTL&&l.nameAr?l.nameAr:l.name}));

  const onModuleChange=(v)=>setForm(p=>({...p,sourceModule:v,categoryId:"",typeId:"",taskType:"",priority:p.priority}));
  const onCategoryChange=(catId)=>{
    const kids=getChildTaxonomy(state,platformCtx,catId);
    const first=kids[0];
    setForm(p=>({...p,categoryId:catId,typeId:first?first.id:"",taskType:first?first.value:""}));
  };
  const onTypeChange=(typeId)=>{
    const ent=typeEntries.find(e=>e.id===typeId);
    setForm(p=>({...p,typeId,taskType:ent?ent.value:"",priority:ent?.defaultPriority||p.priority}));
  };

  const save=async()=>{
    if(!form.title.trim()){setError(isRTL?"العنوان مطلوب":"Title is required");return;}
    setError("");
    setSaving(true);
    try{
      const r=await authFetch(`${API}/api/tasks`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        action:"create_task",ws_key:state.wsKey,
        actor_id:state.currentUser?.id||"",actor_name:state.currentUser?.name||"",
        task:{
          title:form.title,title_ar:form.titleAr,description:form.description,description_ar:form.descriptionAr,
          source_module:form.sourceModule,task_type:form.taskType,
          task_type_id:form.typeId||null,
          assigned_to:form.assignedTo||null,assigned_to_name:form.assignedTo?users.find(u=>u.id===form.assignedTo)?.name||"":"",
          loc_id:form.locId||null,priority:form.priority,due_date:form.dueDate||null,scheduled_time:form.scheduledTime||null,
          context_payload:{taxonomyId:form.typeId||null,categoryId:form.categoryId||null},
          requires_approval:selectedEntry?.requiresApproval||undefined,
          requires_expense:selectedEntry?.requiresExpense||undefined,
          created_by:state.currentUser?.id||"",
        },
      })});
      const j=await r.json();
      if(j.error){setError(j.error);setSaving(false);return;}
      if(j.task){
        dispatch({type:"ADD_TASK",task:j.task});
        onClose();
      }else{setError(isRTL?"استجابة غير متوقعة. حاول مرة أخرى.":"Unexpected response. Please try again.");}
    }catch(e){console.error(e);setError(isRTL?"خطأ في الشبكة. حاول مرة أخرى.":"Network error. Please try again.");}
    setSaving(false);
  };

  return(
    <Modal><div className="stf-detail-overlay" onClick={onClose}>
      <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
        <div className="stf-detail-handle"/>
        <div className="stf-detail-header">
          <div className="stf-detail-title">{isRTL?"مهمة جديدة":"New Task"}</div>
          <button className="stf-detail-close" onClick={onClose}>&#10005;</button>
        </div>
        <div className="stf-detail-body">
          {error&&<div style={{padding:"8px 12px",background:"var(--red-bg)",border:"1px solid var(--red-bd)",borderRadius:8,fontSize:13,color:"var(--red)",marginBottom:12}}>{error}</div>}
          <div className="fr">
            <div className="fg" style={{flex:2}}><label className="fl">{isRTL?"العنوان":"Title"}</label><input className="fi" value={form.title} onChange={e=>{setForm(p=>({...p,title:e.target.value}));setError("");}} placeholder={isRTL?"عنوان المهمة":"Task title"} autoFocus/></div>
            <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الأولوية":"Priority"}</label><SearchableSelect options={priorityOptions} value={form.priority} onChange={v=>setForm(p=>({...p,priority:v||"medium"}))} placeholder="—"/></div>
          </div>
          {isRTL&&<div className="fg"><label className="fl">العنوان (عربي)</label><input className="fi" dir="rtl" value={form.titleAr} onChange={e=>setForm(p=>({...p,titleAr:e.target.value}))}/></div>}
          <div className="fg"><label className="fl">{isRTL?"الوصف":"Description"}</label><textarea className="fta" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder={isRTL?"تفاصيل اختيارية...":"Optional details..."} style={{minHeight:56}}/></div>
          <div className="fr">
            <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"القسم":"Module"}</label><SearchableSelect options={moduleOptions} value={form.sourceModule} onChange={onModuleChange} placeholder={isRTL?"— اختر —":"— select —"}/></div>
            {isTwoLevel&&(
              <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الفئة":"Category"}</label><SearchableSelect options={categoryOptions} value={form.categoryId} onChange={onCategoryChange} placeholder={isRTL?"— اختر —":"— select —"}/></div>
            )}
            <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"نوع المهمة":"Task Type"}</label><SearchableSelect options={typeOptions} value={form.typeId} onChange={onTypeChange} placeholder={isRTL?"— اختر —":"— select —"}/></div>
          </div>
          <div className="fr">
            <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"مسند إلى":"Assign To"}</label><SearchableSelect options={assigneeOptions} value={form.assignedTo} onChange={v=>setForm(p=>({...p,assignedTo:v}))} placeholder={isRTL?"— غير معين —":"— unassigned —"}/></div>
            <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الموقع":"Location"}</label><SearchableSelect options={locationOptions} value={form.locId} onChange={v=>setForm(p=>({...p,locId:v}))} placeholder={isRTL?"— أي —":"— any —"}/></div>
          </div>
          <div className="fr">
            <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"تاريخ الاستحقاق":"Due Date"}</label><input className="fi" type="date" value={form.dueDate} onChange={e=>setForm(p=>({...p,dueDate:e.target.value}))}/></div>
            <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الوقت المحدد":"Scheduled Time"}</label><input className="fi" type="datetime-local" value={form.scheduledTime} onChange={e=>setForm(p=>({...p,scheduledTime:e.target.value}))}/></div>
          </div>
          {(matchedConfig||selectedEntry)&&<div style={{padding:"10px 14px",background:"var(--g50)",border:"1px solid var(--g200)",borderRadius:12,fontSize:12,color:"var(--g500)",marginTop:10,lineHeight:1.6}}>
            <span style={{fontWeight:600,color:"var(--g600)"}}>{isRTL?"تطبيق الإعدادات":"Config matched"}:</span> {matchedConfig?.label||lbl(selectedEntry)||form.taskType}
            {(matchedConfig?.requiresApproval||selectedEntry?.requiresApproval)&&<span> · {isRTL?"يتطلب موافقة":"Requires Approval"}</span>}
            {(matchedConfig?.requiresExpense||selectedEntry?.requiresExpense)&&<span> · {isRTL?"يتطلب مصروف":"Requires Expense"}</span>}
            {(matchedConfig?.defaultPriority||selectedEntry?.defaultPriority)&&<span> · {isRTL?"الأولوية الافتراضية":"Default Priority"}: {matchedConfig?.defaultPriority||selectedEntry?.defaultPriority}</span>}
          </div>}
        </div>
        <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
          <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>{isRTL?"إلغاء":"Cancel"}</button>
          <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={save} disabled={saving}>{saving?(isRTL?"جارٍ الحفظ...":"Saving..."):(isRTL?"إنشاء مهمة":"Create Task")}</button>
        </div>
      </div>
    </div></Modal>
  );
}

export default TaskCreateModal;
